import { CommentAuditEntry } from "types/comments.types"
import { UTF8_BOM } from "utils/files/downloadUtils"

/**
 * Escapes a value for CSV format
 * Handles quotes, commas, and newlines
 */
const escapeCsvValue = (value: string | undefined | null): string => {
  if (value === undefined || value === null) {
    return ""
  }
  const stringValue = String(value)
  // If the value contains quotes, commas, or newlines, wrap in quotes and escape internal quotes
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return stringValue
}

/**
 * Formats a date string to a readable format
 */
const formatDate = (isoDate: string | undefined | null): string => {
  if (!isoDate) return ""
  try {
    const date = new Date(isoDate)
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return isoDate
    }
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  } catch {
    return isoDate || ""
  }
}

/**
 * Maps action codes to human-readable labels
 */
const getActionLabel = (action: CommentAuditEntry["action"]): string => {
  const actionLabels: Record<CommentAuditEntry["action"], string> = {
    created: "Comment Created",
    updated: "Comment Updated",
    replied: "Reply Added",
    status_changed: "Status Changed",
    resolved: "Comment Resolved",
    deleted: "Comment Deleted",
    text_deleted: "Associated Text Deleted",
  }
  return actionLabels[action] || action
}

/**
 * Converts audit log entries to CSV format
 */
const auditLogToCsv = (entries: CommentAuditEntry[]): string => {
  const headers = [
    "Timestamp",
    "Action",
    "Performed By",
    "Comment ID",
    "Previous Value",
    "New Value",
    "Entry ID",
  ]

  // Sort entries by timestamp (newest first) before converting to rows
  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.performedAt || 0).getTime()
    const dateB = new Date(b.performedAt || 0).getTime()
    // Handle invalid dates - put them at the end
    if (isNaN(dateA) && isNaN(dateB)) return 0
    if (isNaN(dateA)) return 1
    if (isNaN(dateB)) return -1
    return dateB - dateA
  })

  const rows = sortedEntries.map((entry) => [
    escapeCsvValue(formatDate(entry.performedAt)),
    escapeCsvValue(getActionLabel(entry.action)),
    escapeCsvValue(entry.performedByName),
    escapeCsvValue(entry.commentId),
    escapeCsvValue(entry.previousValue),
    escapeCsvValue(entry.newValue),
    escapeCsvValue(entry.id),
  ])

  const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n")

  return csvContent
}

/**
 * Triggers a download of a CSV file
 */
const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([UTF8_BOM + csvContent], { type: "text/csv;charset=utf-8;" })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export interface ExportAuditLogOptions {
  filename?: string
  projectName?: string
}

/**
 * Exports comment audit log entries to a CSV file
 *
 * @param entries - Array of audit log entries to export
 * @param options - Export options including filename
 * @returns true if export was successful
 */
export const exportAuditLogToCsv = (
  entries: CommentAuditEntry[],
  options: ExportAuditLogOptions = {}
): boolean => {
  try {
    if (!entries || entries.length === 0) {
      console.warn("No audit log entries to export")
      return false
    }

    const timestamp = new Date().toISOString().split("T")[0]
    const projectPart = options.projectName ? `_${options.projectName}` : ""
    const filename = options.filename || `comment_audit_log${projectPart}_${timestamp}`

    const csvContent = auditLogToCsv(entries)
    downloadCsv(csvContent, filename)

    return true
  } catch (error) {
    console.error("Error exporting audit log to CSV:", error)
    return false
  }
}

/**
 * Generic CSV export for array of objects
 */
export const exportToCsv = <T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  options: {
    headers?: string[]
    columns?: (keyof T)[]
  } = {}
): boolean => {
  try {
    if (!data || data.length === 0) {
      console.warn("No data to export")
      return false
    }

    const columns = options.columns || (Object.keys(data[0]) as (keyof T)[])
    const headers = options.headers || columns.map(String)

    const rows = data.map((item) =>
      columns.map((col) => escapeCsvValue(String(item[col] ?? "")))
    )

    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n")

    downloadCsv(csvContent, filename)
    return true
  } catch (error) {
    console.error("Error exporting to CSV:", error)
    return false
  }
}
